import React, { useLayoutEffect, useState, useRef, useEffect } from "react";
import "../../css/superTreeview.css";

import Loader from "../LoadSpinner";
import { IconButton } from "@fluentui/react/lib/Button";
import {
  Label,
  Text,
  Sticky,
  StickyPositionType,
  ConstrainMode,
  ScrollablePane,
  ScrollbarVisibility,
  TooltipHost,
  DirectionalHint,
} from "@fluentui/react";
import { NO_CLASSFORMAT_FOUND } from "../../redux/constants/messageTypes";
import {
  OBJECT_SELECT_TYPE,
  DOCUMENT_LOAD_STATUS,
  REFRESH_TOKEN_ACTION,
  RESPONSE_TYPE,
} from "../../redux/constants/metaData";
import {
  DetailsList,
  DetailsListLayoutMode,
  Selection,
  SelectionMode,
} from "@fluentui/react/lib/DetailsList";
import { useCheckin } from "../../redux/hooks/useCheckin";
import { useTools } from "../../redux/hooks/useTools";
import { useCommonControlsStyle } from "../../theme/styles/CommonControlsStyle";
import { useTheme } from "../../theme/OAS365Theme";
import { useGridStyle } from "../../theme/styles/GridStyle";
import { logger } from "../../utils/logger";
import addIcon from "../../assets/addCircledOutline.svg";
import infoIconSvg from "../../assets/infoCircledOutline.svg";
import addCircledOutlineDisabled from "../../assets/addCircledOutlineDisabled.svg";
import noContentIcon from "../../assets/svg/noSearchResults.svg";

const getKey = (item, index) => {
  return item.key;
};

const RecentObjectsView = ({
  embedded = false,
  onEmbeddedFavoriteAdded,
}) => {
  const {
    recentObjectConnection,
    selectedObjectConnections,
    recentObjectsSelected,
    advSearchSelectedObjs,
    recentObjectDataLoadStatus,
    recentObjectDataErrorMessage,
    getRecentObjects,
  } = useCheckin();
  const {
    onRecentObjectSelected,
    onRecentObjectSelectedDescription,
    onSelectedObjectConnectionChanged,
    onRecentObjectDataLoadStatusChanged,
  } = useTools();
  const theme = useTheme();
  const sbWidth = 12;
  const sbHeight = 10;
  const gridThemeStyle = useGridStyle();
  const binIcon = { iconName: "Refresh" };

  const loaderBorderStyle = {
    padding: "10px autopx",
    bottom: "50px",
    marginTop: "20px",
  };
  const messageBorderStyle = {
    margin: "auto",
    width: "200px",
    paddingLeft: "0px",
  };
  const styleTheme = useCommonControlsStyle();
  const [screenWidth, setScreenWidth] = useState(
    isWideScreen(window.innerWidth),
  );

  const [addButtonClicked, setAddButtonClicked] = useState(false);
  useLayoutEffect(() => {
    if (
      recentObjectConnection !== undefined &&
      recentObjectConnection !== null &&
      recentObjectConnection.length > 0
    ) {
      onRecentObjectDataLoadStatusChanged(DOCUMENT_LOAD_STATUS.onCompletion);
      return;
    }
    if (
      recentObjectDataLoadStatus === DOCUMENT_LOAD_STATUS.onCompletion ||
      recentObjectDataLoadStatus === DOCUMENT_LOAD_STATUS.noData
    ) {
      return;
    }
    onRecentObjectDataLoadStatusChanged(DOCUMENT_LOAD_STATUS.onLoading);
    getRecentObjects({
      retry: 0,
      actionType: REFRESH_TOKEN_ACTION.recentlyUsedObjectsLoad,
      refreshResponse: RESPONSE_TYPE.none,
    });
  }, []);

  const cellTextStyle = {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "inline-block",
    maxWidth: "100%",
    fontFamily: theme.fonts?.regular,
    fontSize: `${theme.fontSizes?.normal ?? 12}px`,
  };

  const columns = [
    {
      key: "NameDesc",
      fieldName: "NameDesc",
      name: "Object Type",
      minWidth: 120,
      maxWidth: 160,
      isRowHeader: true,
      isResizable: true,
      isSorted: false,
      isSortedDescending: false,
      sortAscendingAriaLabel: "Sorted A to Z",
      sortDescendingAriaLabel: "Sorted Z to A",
      styles: gridThemeStyle.headerCellStyle,

      onRender: (item) => (
        <TooltipHost
          content={item.nameDesc}
          directionalHint={DirectionalHint.bottomCenter}
        >
          <span style={cellTextStyle}>{item.nameDesc}</span>
        </TooltipHost>
      ),
      isPadded: true,
    },
    {
      key: "KeyDesc",
      fieldName: "KeyDesc",
      name: "Object Key",
      minWidth: 140,
      maxWidth: 180,
      isRowHeader: true,
      isResizable: true,
      isSorted: false,
      isSortedDescending: false,
      sortAscendingAriaLabel: "Sorted A to Z",
      sortDescendingAriaLabel: "Sorted Z to A",
      styles: gridThemeStyle.headerCellStyle,

      onRender: (item) => (
        <TooltipHost
          content={item.keyDesc}
          directionalHint={DirectionalHint.bottomCenter}
        >
          <span style={cellTextStyle}>{item.keyDesc}</span>
        </TooltipHost>
      ),
      isPadded: true,
    },
    {
      key: "DocObjDesc",
      fieldName: "DocObjDesc",
      name: "Description",
      minWidth: 140,
      maxWidth: 180,
      isRowHeader: true,
      isResizable: true,
      isSorted: false,
      isSortedDescending: false,
      sortAscendingAriaLabel: "Sorted A to Z",
      sortDescendingAriaLabel: "Sorted Z to A",
      styles: gridThemeStyle.headerCellStyle,

      onRender: (item) => (
        <TooltipHost
          content={item.docObjDesc}
          directionalHint={DirectionalHint.bottomCenter}
        >
          <span style={cellTextStyle}>{item.docObjDesc}</span>
        </TooltipHost>
      ),
      isPadded: true,
    },
    {
      key: "Add",
      fieldName: "AddObj",
      name: "",
      minWidth: 40,
      maxWidth: 48,
      isRowHeader: false,
      isResizable: false,
      isSorted: false,
      isSortedDescending: false,
      sortAscendingAriaLabel: "Sorted A to Z",
      sortDescendingAriaLabel: "Sorted Z to A",
      styles: gridThemeStyle.headerCellStyle,
      onRender: (item) => {
        if (item.isSelected) {
          return (
            <span
              styles={styleTheme.AddObjButtonFillStyle}
              disabled={item.isSelected}
              onClick={() => {
                setAddButtonClicked(true);
                return new Selection({
                  onSelectionChanged: () => getSelectionDetails(),
                  getKey: (item) => item.key,
                });
              }}
            >
              <img
                src={addCircledOutlineDisabled}
                alt="Added"
                title="Added"
                style={{
                  marginTop: 0,
                  paddingLeft: 4,
                  paddingRight: 4,
                  width: "28px",
                }}
              />
            </span>
          );
        } else {
          return (
            <span
              styles={styleTheme.AddObjButtonFillStyle}
              disabled={item.isSelected}
              onClick={() => {
                setAddButtonClicked(true);
                return new Selection({
                  onSelectionChanged: () => getSelectionDetails(),
                  getKey: (item) => item.key,
                });
              }}
            >
              <img
                src={addIcon}
                alt="Add"
                title="Add"
                style={{
                  marginTop: 0,
                  paddingLeft: 4,
                  paddingRight: 4,
                  cursor: "pointer",
                  width: "28px",
                }}
              />
            </span>
          );
        }
      },
      isPadded: true,
    },
  ];

  const columnsMiniScreen = [
    {
      key: "NameDesc",
      fieldName: "NameDesc",
      name: "Object Type",
      minWidth: 120,
      maxWidth: 160,
      isRowHeader: true,
      isResizable: true,
      isSorted: false,
      isSortedDescending: false,
      sortAscendingAriaLabel: "Sorted A to Z",
      sortDescendingAriaLabel: "Sorted Z to A",
      styles: gridThemeStyle.headerCellStyle,

      onRender: (item) => (
        <TooltipHost
          content={item.nameDesc}
          directionalHint={DirectionalHint.bottomCenter}
        >
          <span style={cellTextStyle}>{item.nameDesc}</span>
        </TooltipHost>
      ),
      isPadded: true,
    },
    {
      key: "DocObjDesc",
      fieldName: "DocObjDesc",
      name: "Description",
      minWidth: 140,
      maxWidth: 180,
      isRowHeader: true,
      isResizable: true,
      isSorted: false,
      isSortedDescending: false,
      sortAscendingAriaLabel: "Sorted A to Z",
      sortDescendingAriaLabel: "Sorted Z to A",
      styles: gridThemeStyle.headerCellStyle,
      onRender: (item) => (
        <TooltipHost
          content={item.docObjDesc}
          directionalHint={DirectionalHint.bottomCenter}
        >
          <span style={cellTextStyle}>{item.docObjDesc}</span>
        </TooltipHost>
      ),
      isPadded: true,
    },
    {
      key: "ObjectKeyToolTip",
      minWidth: 30,
      maxWidth: 30,
      //onColumnClick: onColumnClick,
      onRender: (selectedItems) => {
        return (
          <TooltipHost
            content={
              <div style={{ display: "flex", alignItems: "center" }}>
                <div style={{ flexDirection: "column" }}>
                  <p
                    style={{
                      margin: 0,
                      fontFamily: theme.fonts?.regular,
                      fontSize: `${theme.fontSizes?.normal ?? 12}px`,
                    }}
                  >
                    {selectedItems.keyDesc}
                  </p>
                </div>
              </div>
            }
            tooltipProps={{
              calloutProps: {
                styles: {
                  fontColor: theme.colors.addovationWhite,
                  beak: {
                    background: theme.colors.addovation70Green,
                    color: theme.colors.addovationWhite,
                  },
                  beakCurtain: {
                    background: theme.colors.addovation70Green,
                    color: theme.colors.addovationWhite,
                  },
                  calloutMain: {
                    background: theme.colors.addovation70Green,
                    color: theme.colors.addovationWhite,
                  },
                },
              },
              styles: {
                content: {
                  color: theme.colors.addovationWhite,
                  fontFamily: theme.fonts?.regular,
                  fontSize: `${theme.fontSizes?.normal ?? 12}px`,
                },
              },
            }}
          >
            <img src={infoIconSvg} alt="Info" style={{ width: "20px" }} />
          </TooltipHost>
        );
      },
    },
    {
      key: "Add",
      fieldName: "AddObj",
      name: "",
      minWidth: 40,
      maxWidth: 48,
      isRowHeader: false,
      isResizable: false,
      isSorted: false,
      isSortedDescending: false,
      sortAscendingAriaLabel: "Sorted A to Z",
      sortDescendingAriaLabel: "Sorted Z to A",
      styles: gridThemeStyle.headerCellStyle,
      onRender: (item) => {
        if (item.isSelected) {
          return (
            <span
              styles={styleTheme.AddObjButtonFillStyle}
              disabled={item.isSelected}
              onClick={() => {
                setAddButtonClicked(true);
                return new Selection({
                  onSelectionChanged: () => getSelectionDetails(),
                  getKey: (item) => item.key,
                });
              }}
            >
              <img
                src={addCircledOutlineDisabled}
                alt="Added"
                title="Added"
                style={{
                  marginTop: -3,
                  paddingLeft: 4,
                  paddingRight: 4,
                  width: "28px",
                }}
              />
            </span>
          );
        } else {
          return (
            <span
              styles={styleTheme.AddObjButtonFillStyle}
              disabled={item.isSelected}
              onClick={() => {
                setAddButtonClicked(true);
                return new Selection({
                  onSelectionChanged: () => getSelectionDetails(),
                  getKey: (item) => item.key,
                });
              }}
            >
              <img
                src={addIcon}
                alt="Add"
                title="Add"
                style={{
                  marginTop: -3,
                  paddingLeft: 4,
                  paddingRight: 4,
                  cursor: "pointer",
                  width: "28px",
                }}
              />
            </span>
          );
        }
      },
      isPadded: true,
    },
  ];

  const getSelectionDetails = () => {
    //onRecentObjectConnectionSelect(selection.getSelection());
    // selection.getSelection().forEach(element => {
    //   selectedObjs.push(element);
    // });
    // const newColumns = tableColumns.slice();
    // onRecentObjectSelected(selectedObjs);
    // setTableColumns(newColumns);
  };

  const createSelection = () => {
    return new Selection({
      onSelectionChanged: () => getSelectionDetails(),
      getKey: (item) => item.key,
    });
  };

  const [tableColumns, setTableColumns] = useState(columns);
  const [tableColumnsMiniScreen, setTableColumnsMiniScreen] =
    useState(columnsMiniScreen);
  const [tableItems, setTableItems] = useState([]);
  const [selection, setSelection] = useState(createSelection());

  const refItems = useRef(tableItems);

  useLayoutEffect(() => {
    if (recentObjectConnection) {
      logger.debug(
        "RecentObjectsView build items from recentObjectConnection",
        {
          recentObjectConnectionCount: Array.isArray(recentObjectConnection)
            ? recentObjectConnection.length
            : null,
          selectedObjectConnectionsCount: Array.isArray(selectedObjectConnections)
            ? selectedObjectConnections.length
            : null,
        },
        "CHECKIN"
      );
      const items = generateRecentObjects(recentObjectConnection);
      const selectedConnections = Array.isArray(selectedObjectConnections)
        ? selectedObjectConnections
        : [];
      selectedConnections.forEach((selectedConn) => {
        items.forEach((recentConn) => {
          if (
            selectedConn.keyRef == recentConn.keyRef &&
            selectedConn.luName == recentConn.luName
          ) {
            recentConn.isSelected = true;
          }
        });
      });
      updateItems(items, false);
    }
  }, [recentObjectConnection]);

  useEffect(() => {
    if (!Array.isArray(recentObjectConnection) || recentObjectConnection.length === 0) {
      return;
    }
    setTableItems((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      return prev.map((row) => {
        const isSel = Array.isArray(selectedObjectConnections)
          ? selectedObjectConnections.some(
              (s) => s.keyRef === row.keyRef && s.luName === row.luName,
            )
          : false;
        if (row.isSelected === isSel) return row;
        return { ...row, isSelected: isSel };
      });
    });
  }, [selectedObjectConnections, recentObjectConnection]);

  useEffect(() => {
    if (addButtonClicked) {
      const selectedRows = selection.getSelection();
      logger.debug(
        "RecentObjectsView add button clicked",
        {
          selectedRowsCount: Array.isArray(selectedRows) ? selectedRows.length : 0,
          recentObjectsSelectedCount: Array.isArray(recentObjectsSelected)
            ? recentObjectsSelected.length
            : null,
          advSearchSelectedObjsCount: Array.isArray(advSearchSelectedObjs)
            ? advSearchSelectedObjs.length
            : null,
        },
        "CHECKIN"
      );
      if (!selectedRows || selectedRows.length === 0) {
        setAddButtonClicked(false);
        return;
      }

      const nextRecentSelected = Array.isArray(recentObjectsSelected)
        ? [...recentObjectsSelected]
        : [];

      selectedRows.forEach((selectedRow) => {
        const exists = nextRecentSelected.some(
          (obj) =>
            obj.keyRef === selectedRow.keyRef && obj.luName === selectedRow.luName,
        );

        if (!exists) {
          nextRecentSelected.push({ ...selectedRow, isSelected: true });
        }
        onRecentObjectSelectedDescription(selectedRow.nameDesc);
      });

      onRecentObjectSelected(nextRecentSelected);
      onSelectedObjectConnectionChanged(
        nextRecentSelected,
        Array.isArray(advSearchSelectedObjs) ? [...advSearchSelectedObjs] : [],
      );
      logger.debug(
        "RecentObjectsView updated selected object connections",
        {
          nextRecentSelectedCount: nextRecentSelected.length,
        },
        "CHECKIN"
      );

      setTableItems((prevItems) =>
        prevItems.map((item) => {
          const isSelected = nextRecentSelected.some(
            (selectedRow) =>
              selectedRow.keyRef === item.keyRef &&
              selectedRow.luName === item.luName,
          );
          return isSelected ? { ...item, isSelected: true } : item;
        }),
      );
      setAddButtonClicked(false);
    }
  }, [addButtonClicked]);

  useEffect(() => {
    function handleResize() {
      setScreenWidth(isWideScreen(window.innerWidth));
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  function isWideScreen(width) {
    if (width > 1023) {
      return true;
    } else {
      return false;
    }
  }

  function generateRecentObjects(resObjects) {
    const items = [];
    if (resObjects.length !== 0) {
      resObjects.forEach((item) => {
        items.push({
          isSelected: false,
          key: `${item.luName}-${item.keyRef}`,
          keyRef: item.keyRef,
          nameDesc: item.nameDesc,
          keyDesc: item.keyDesc,
          luName: item.luName,
          docObjDesc: item.docObjectDesc,
          selectType: OBJECT_SELECT_TYPE.recent,
        });
      });
    }
    return items;
  }

  const updateItems = (newItems, columnClicked) => {
    refItems.current = newItems;
    setTableItems(newItems);
  };

  const handleEmbeddedAdd = (item) => {
    if (item.isSelected) return;
    const nextRecentSelected = Array.isArray(recentObjectsSelected)
      ? [...recentObjectsSelected]
      : [];
    const exists = nextRecentSelected.some(
      (obj) => obj.keyRef === item.keyRef && obj.luName === item.luName,
    );
    if (!exists) {
      nextRecentSelected.push({ ...item, isSelected: true });
    }
    if (embedded) {
      onEmbeddedFavoriteAdded?.();
    } else {
      onRecentObjectSelectedDescription(item.nameDesc);
    }
    onRecentObjectSelected(nextRecentSelected);
    onSelectedObjectConnectionChanged(
      nextRecentSelected,
      Array.isArray(advSearchSelectedObjs) ? [...advSearchSelectedObjs] : [],
    );
    setTableItems((prevItems) =>
      prevItems.map((row) =>
        row.keyRef === item.keyRef && row.luName === item.luName
          ? { ...row, isSelected: true }
          : row,
      ),
    );
    logger.debug(
      "RecentObjectsView embedded add",
      { luName: item.luName, keyRef: item.keyRef },
      "CHECKIN",
    );
  };

  const renderEmptyRecentObjects = () => (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
        minHeight: embedded ? 140 : 280,
      }}
    >
      <div style={{ textAlign: "center", width: 400 }}>
        <Text
                variant="medium"
                styles={{
                  root: {
                    color: "#4c8d6a",
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 600,
                    display: "block",
                    width: "100%",
                    textAlign: "center",
                    marginBottom: embedded ? 0 : 16,
                    fontSize: `${theme.fontSizes?.normal ?? 12}px`,
                  },
                }}
              >
                No Recently used object connection.
              </Text>
        {!embedded && (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <img
              src={noContentIcon}
              style={{
                width: 240,
                height: 240,
                objectFit: "contain",
              }}
              alt="No recent objects"
            />
          </div>
        )}
      </div>
    </div>
  );

  const visibleItems = tableItems.filter((item) => !item.isSelected);
  const allFavoritesSelected =
    Array.isArray(tableItems) &&
    tableItems.length > 0 &&
    visibleItems.length === 0;

  const renderAllSelectedMessage = () => (
    <div
      style={{
        padding: "20px 16px",
        textAlign: "center",
        width: "100%",
      }}
    >
      <Text
        variant="medium"
        styles={{
          root: {
            color: "#4c8d6a",
            fontFamily: "Poppins, sans-serif",
            fontWeight: 600,
            display: "block",
            width: "100%",
            textAlign: "center",
            fontSize: `${theme.fontSizes?.normal ?? 12}px`,
          },
        }}
      >
        All recent objects are already selected
      </Text>
    </div>
  );

  switch (recentObjectDataLoadStatus) {
    case DOCUMENT_LOAD_STATUS.onCompletion:
      if (!Array.isArray(tableItems) || tableItems.length === 0) {
        return renderEmptyRecentObjects();
      }
      if (allFavoritesSelected) {
        return renderAllSelectedMessage();
      }
      if (embedded) {
        const cardBg = "#E8F5E9";
        /** Mint cards: always dark text (theme fontColor is light in dark mode). */
        const textColor = "#000000";
        return (
          <div style={{ marginTop: 0, width: "100%" }}>
            {visibleItems.map((item) => (
              <div
                key={item.key ?? `${item.luName}-${item.keyRef}`}
                style={{
                  backgroundColor: cardBg,
                  padding: "8px 14px",
                  marginBottom: 10,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 14,
                }}
              >
                <div
                  style={{
                    minWidth: 0,
                    flex: 1,
                    textAlign: "left",
                    cursor: "default",
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: 12,
                      color: textColor,
                      fontFamily: theme.fonts?.regular,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {item.nameDesc ?? ""}
                  </div>
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: 11,
                      color: textColor,
                      fontFamily: theme.fonts?.regular,
                      marginTop: 4,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {item.docObjDesc ?? ""}
                  </div>
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: 11,
                      color: textColor,
                      fontFamily: theme.fonts?.regular,
                      marginTop: 4,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {item.keyDesc ?? ""}
                  </div>
                </div>
                <button
                  type="button"
                  title={item.isSelected ? "Added" : "Add"}
                  aria-label={item.isSelected ? "Already added" : "Add object connection"}
                  disabled={item.isSelected}
                  style={{
                    flexShrink: 0,
                    border: "none",
                    background: "transparent",
                    cursor: item.isSelected ? "default" : "pointer",
                    padding: 4,
                    lineHeight: 0,
                  }}
                  onClick={() => handleEmbeddedAdd(item)}
                >
                  <img
                    src={item.isSelected ? addCircledOutlineDisabled : addIcon}
                    alt=""
                    style={{
                      width: 28,
                      height: 28,
                      display: "block",
                      objectFit: "contain",
                    }}
                  />
                </button>
              </div>
            ))}
          </div>
        );
      }
      if (screenWidth === true) {
        return (
          <ScrollablePane
            scrollbarVisibility={ScrollbarVisibility.auto}
            styles={{
              root: {
                selectors: {
                  ".ms-ScrollablePane--contentContainer": {
                    scrollbarWidth: sbWidth,
                    scrollbarColor: `${theme.colors.scrollbarThumb} ${theme.colors.scrollbarBackground}`,
                    paddingRight: sbWidth + 4,
                    boxSizing: "border-box",
                  },
                  ".ms-ScrollablePane--contentContainer::-webkit-scrollbar": {
                    width: sbWidth,
                    height: sbHeight,
                  },
                  ".ms-ScrollablePane--contentContainer::-webkit-scrollbar-track":
                    {
                      background: theme.colors.scrollbarBackground,
                    },
                  ".ms-ScrollablePane--contentContainer::-webkit-scrollbar-thumb":
                    {
                      background: theme.colors.scrollbarThumb,
                    },
                },
                height: "calc(100vh - 205px)",
                marginTop: 95,
              },
            }}
          >
            <DetailsList
              styles={gridThemeStyle.recentlyUsedGridStyle}
              items={visibleItems}
              compact={false}
              columns={tableColumns}
              selectionMode={SelectionMode.single}
              getKey={getKey}
              layoutMode={DetailsListLayoutMode.fixedColumns}
              constrainMode={ConstrainMode.horizontalConstrained}
              isHeaderVisible={true}
              selection={selection}
              selectionPreservedOnEmptyClick={true}
              enterModalSelectionOnTouch={true}
              ariaLabelForSelectionColumn="Toggle selection"
              ariaLabelForSelectAllCheckbox="Toggle selection for all items"
              checkButtonAriaLabel="select row"
              visibility="false"
              onRenderDetailsHeader={(headerProps, defaultRender) => {
                return (
                  <Sticky
                    stickyPosition={StickyPositionType.Header}
                    isScrollSynced={true}
                    stickyBackgroundColor="transparent"
                  >
                    {defaultRender({
                      ...headerProps,
                      styles: gridThemeStyle.headerStyle,
                    })}
                  </Sticky>
                );
              }}
            />
          </ScrollablePane>
        );
      } else {
        return (
          <div
            style={{
              position: "relative",
              minHeight: 300,
              maxHeight: (document.documentElement.offsetHeight / 100) * 65,
              // background: "#fff",
            }}
          >
            <ScrollablePane
              scrollbarVisibility={ScrollbarVisibility.auto}
              styles={{
                root: {
                  selectors: {
                    ".ms-ScrollablePane--contentContainer": {
                      scrollbarWidth: sbWidth,
                      scrollbarColor: `${theme.colors.scrollbarThumb} ${theme.colors.scrollbarBackground}`,
                      paddingRight: sbWidth + 4,
                      boxSizing: "border-box",
                    },
                    ".ms-ScrollablePane--contentContainer::-webkit-scrollbar": {
                      width: sbWidth,
                      height: sbHeight,
                    },
                    ".ms-ScrollablePane--contentContainer::-webkit-scrollbar-track":
                      {
                        background: theme.colors.scrollbarBackground,
                      },
                    ".ms-ScrollablePane--contentContainer::-webkit-scrollbar-thumb":
                      {
                        background: theme.colors.scrollbarThumb,
                      },
                  },
                },
              }}
            >
              <DetailsList
                styles={gridThemeStyle.recentlyUsedGridStyle}
                items={visibleItems}
                compact={false}
                columns={tableColumnsMiniScreen}
                selectionMode={SelectionMode.single}
                getKey={getKey}
                layoutMode={DetailsListLayoutMode.fixedColumns}
                constrainMode={ConstrainMode.horizontalConstrained}
                isHeaderVisible={true}
                selection={selection}
                selectionPreservedOnEmptyClick={true}
                enterModalSelectionOnTouch={true}
                ariaLabelForSelectionColumn="Toggle selection"
                ariaLabelForSelectAllCheckbox="Toggle selection for all items"
                checkButtonAriaLabel="select row"
                visibility="false"
                onRenderDetailsHeader={(headerProps, defaultRender) => {
                  return (
                    <Sticky
                      stickyPosition={StickyPositionType.Header}
                      isScrollSynced={true}
                      stickyBackgroundColor="transparent"
                    >
                      {defaultRender({
                        ...headerProps,
                        styles: gridThemeStyle.headerStyle,
                      })}
                    </Sticky>
                  );
                }}
              />
            </ScrollablePane>
          </div>
        );
      }

    case DOCUMENT_LOAD_STATUS.onLoading:
      return (
        <>
          <div style={loaderBorderStyle}>
            <div
              style={{
                margin: "auto",
                width: "50px",
                paddingLeft: "0px",
              }}
            >
              <Loader />
            </div>
          </div>
        </>
      );
    case DOCUMENT_LOAD_STATUS.noData:
      return (
        <>
          <div style={loaderBorderStyle}>
            <div style={messageBorderStyle}>
              <IconButton
                style={{ marginLeft: "80px" }}
                iconProps={binIcon}
                title="Refresh"
                ariaLabel="Refresh"
                onClick={(ev) => {
                  getRecentObjects({
                    retry: 0,
                    actionType: REFRESH_TOKEN_ACTION.recentlyUsedObjectsLoad,
                    refreshResponse: RESPONSE_TYPE.none,
                  });
                }}
              />
              <Label style={styleTheme.LabelInformationStyle}>
                {NO_CLASSFORMAT_FOUND}
              </Label>
            </div>
          </div>
        </>
      );
    case DOCUMENT_LOAD_STATUS.onError:
      return (
        <>
          <div style={loaderBorderStyle}>
            <div style={messageBorderStyle}>
              <IconButton
                style={{ marginLeft: "80px" }}
                iconProps={binIcon}
                title="Refresh"
                ariaLabel="Refresh"
                onClick={(ev) => {
                  getRecentObjects({
                    retry: 0,
                    actionType: REFRESH_TOKEN_ACTION.recentlyUsedObjectsLoad,
                    refreshResponse: RESPONSE_TYPE.none,
                  });
                }}
              />
              <Label style={styleTheme.LabelErrorMessageStyle}>
                {recentObjectDataErrorMessage}
              </Label>
            </div>
          </div>
        </>
      );
    default:
      return <></>;
  }
};

export default RecentObjectsView;
